"use client";

import { ArrowRight, Loader2, Search } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/design-system/input";
import { AuthenticationStep } from "@/lib/analytics/segmentEvents";
import { trackAuthFlow } from "@/lib/analytics/trackEvents";
import type { DiscoverySessionData } from "@/lib/SessionServices";
import type { DiscoveredOrganizations } from "@/lib/StytchLib";
import type { DiscoveryOrganizationTypesResponse } from "@/lib/types/discovery";
import { WORKSPACE_AVATAR_GRADIENT } from "@/lib/workspaceAvatar";
import {
  LinkOldAccountCard,
  type PendingAccountLink,
} from "./LinkOldAccountCard";

const AUTOMATE_ONBOARDING = true;

export type Props = {
  discovered_organizations: DiscoveredOrganizations;
  discoverySessionData?: DiscoverySessionData;
  pendingAccountLink?: PendingAccountLink | null;
};

type OrgRow = DiscoveredOrganizations[number] & {
  isAgency: boolean;
  logoUrl: string | null;
};

export const DiscoveredOrganizationsList = ({
  discovered_organizations,
  discoverySessionData,
  pendingAccountLink,
}: Props) => {
  const router = useRouter();

  const [showCreateOptions, setShowCreateOptions] = useState(false);
  const [preferredOrgType, setPreferredOrgType] = useState<string | null>(null);
  const [isAgencyByStytchId, setIsAgencyByStytchId] = useState<
    Record<string, boolean>
  >({});
  const [logoUrlByStytchId, setLogoUrlByStytchId] = useState<
    Record<string, string | null>
  >({});
  const [navigatingToOrgId, setNavigatingToOrgId] = useState<string | null>(
    null,
  );
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (discovered_organizations.length > 0) {
      trackAuthFlow({
        step: AuthenticationStep.SELECT_ORGANIZATION,
        additionalParams: {
          event: "page loaded",
          orgCount: discovered_organizations.length,
          hasPreferredOrgType: Boolean(
            localStorage.getItem("preferredOrgType"),
          ),
        },
      });
    }
  }, [discovered_organizations.length]);

  const discoveredOrganizationIds = useMemo(
    () =>
      discovered_organizations
        .map(({ organization }) => organization?.organization_id)
        .filter((organizationId): organizationId is string =>
          Boolean(organizationId),
        ),
    [discovered_organizations],
  );
  const discoveredOrganizationIdsKey = useMemo(
    () => [...new Set(discoveredOrganizationIds)].sort().join(","),
    [discoveredOrganizationIds],
  );

  useEffect(() => {
    if (discoveredOrganizationIdsKey.length === 0) {
      setIsAgencyByStytchId({});
      setLogoUrlByStytchId({});
      return;
    }

    const abortController = new AbortController();
    const fetchOrganizationTypes = async () => {
      try {
        const resp = await fetch("/api/discovery/organization-types", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            organizationIds: discoveredOrganizationIdsKey.split(","),
          }),
          signal: abortController.signal,
        });
        if (!resp.ok) return;
        const data = (await resp.json()) as DiscoveryOrganizationTypesResponse;
        setIsAgencyByStytchId(data.isAgencyByStytchId ?? {});
        setLogoUrlByStytchId(data.logoUrlByStytchId ?? {});
      } catch (error) {
        if (abortController.signal.aborted) return;
        console.error("Failed to load organization types", error);
      }
    };

    void fetchOrganizationTypes();
    return () => abortController.abort();
  }, [discoveredOrganizationIdsKey]);

  useEffect(() => {
    const savedOrgType = localStorage.getItem("preferredOrgType");
    // A pending account link takes precedence over the create-workspace
    // auto-redirect: linking is what makes the user's organizations appear.
    if (
      savedOrgType &&
      discovered_organizations.length === 0 &&
      !pendingAccountLink
    ) {
      setPreferredOrgType(savedOrgType);
      setShowCreateOptions(true);

      const queryParam =
        discoverySessionData &&
        !discoverySessionData.error &&
        discoverySessionData.isDiscovery &&
        discoverySessionData.intermediateSession
          ? `?intermediate_session=${discoverySessionData.intermediateSession}`
          : "";

      trackAuthFlow({
        step: AuthenticationStep.SELECT_ORGANIZATION,
        additionalParams: {
          event: "auto redirect",
          orgType: savedOrgType,
          queryParam,
        },
      });

      const redirectTimer = setTimeout(() => {
        router.push(
          `/select-organization/${savedOrgType.toLowerCase()}${queryParam}`,
        );
      }, 100);
      return () => clearTimeout(redirectTimer);
    }

    if (savedOrgType) setPreferredOrgType(savedOrgType);
  }, [
    discovered_organizations.length,
    discoverySessionData,
    router,
    pendingAccountLink,
  ]);

  const creatingOrganization =
    showCreateOptions ||
    (discovered_organizations.length === 0 && !discoverySessionData?.error);

  const { agencyMemberships, otherMemberships } = useMemo(() => {
    const withType: OrgRow[] = discovered_organizations.map((entry) => {
      const stytchId = entry.organization?.organization_id ?? "";
      const isAgency = stytchId ? Boolean(isAgencyByStytchId[stytchId]) : false;
      const logoUrl = stytchId ? (logoUrlByStytchId[stytchId] ?? null) : null;
      return { ...entry, isAgency, logoUrl };
    });
    const q = search.trim().toLowerCase();
    const matches = (row: OrgRow) =>
      !q ||
      (row.organization?.organization_name ?? "").toLowerCase().includes(q);
    return {
      agencyMemberships: withType.filter((row) => row.isAgency).filter(matches),
      otherMemberships: withType.filter((row) => !row.isAgency).filter(matches),
    };
  }, [discovered_organizations, isAgencyByStytchId, logoUrlByStytchId, search]);

  const flatRowsLength = agencyMemberships.length + otherMemberships.length;

  const handleOrganizationClick = (
    organizationId: string,
    orgName: string,
    membershipType: string,
  ) => {
    trackAuthFlow({
      step: AuthenticationStep.SELECT_ORGANIZATION,
      additionalParams: {
        event: "organization selected",
        organizationId,
        organizationName: orgName,
        membershipType,
      },
    });
  };

  const handleCreateNewOrg = () => {
    trackAuthFlow({
      step: AuthenticationStep.SELECT_ORGANIZATION,
      additionalParams: {
        event: "create new organization clicked",
        existingOrgCount: discovered_organizations.length,
      },
    });
    setShowCreateOptions(true);
  };

  const handleOrgRowClick = (
    entry:
      | (typeof agencyMemberships)[number]
      | (typeof otherMemberships)[number],
  ) => {
    if (navigatingToOrgId !== null) return;
    const organizationId = entry.organization?.organization_id ?? null;
    setNavigatingToOrgId(organizationId);
    handleOrganizationClick(
      organizationId ?? "",
      entry.organization?.organization_name || "Unknown",
      entry.membership?.type || "Unknown",
    );
    // Reset the first-visit-splash key so the splash plays again on the
    // next page (the user is entering the framework workspace). The splash
    // component re-detects the absence of the key as a fresh visit.
    try {
      sessionStorage.removeItem("nf:tab-splash-shown");
    } catch {
      // sessionStorage blocked — splash will just not show, no harm done.
    }
    window.location.href = `/api/discovery/${organizationId}`;
  };

  const handleOrgTypeSelection = (orgType: string) => {
    trackAuthFlow({
      step: AuthenticationStep.SELECT_ORGANIZATION,
      additionalParams: {
        event: "organization type selected",
        orgType,
      },
    });
  };

  if (creatingOrganization) {
    return (
      <div className="flex flex-col grow w-full px-nf-ds-4 py-nf-ds-8 sm:px-nf-ds-12 sm:py-nf-ds-16">
        <div className="flex flex-col grow max-w-[600px] w-full mx-auto">
          <header className="mb-nf-ds-10">
            <h1 className="font-nf-ds-serif text-[40px] leading-[1.05] tracking-[-0.02em] text-nf-ds-black">
              Create a workspace
            </h1>
            <p className="font-nf-ds-sans text-[15px] text-nf-ds-gray-600 mt-nf-ds-2">
              Pick the workspace type that fits how you operate.
            </p>
          </header>

          {pendingAccountLink && (
            <LinkOldAccountCard
              targetEmail={pendingAccountLink.targetEmail}
              organizationCount={pendingAccountLink.organizationCount}
            />
          )}

          {AUTOMATE_ONBOARDING && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-nf-ds-4">
              <CreateOrgCard
                title="Brand"
                description="A single brand running its own ad accounts."
                href={`/select-organization/brand${
                  discoverySessionData &&
                  "intermediate_session" in discoverySessionData
                    ? `?intermediate_session=${discoverySessionData.intermediate_session}`
                    : ""
                }`}
                onClick={() => handleOrgTypeSelection("Brand")}
                selected={preferredOrgType === "Brand"}
              />
              <CreateOrgCard
                title="Agency"
                description="An agency that manages multiple brands."
                href={`/select-organization/agency${
                  discoverySessionData &&
                  "intermediate_session" in discoverySessionData
                    ? `?intermediate_session=${discoverySessionData.intermediate_session}`
                    : ""
                }`}
                onClick={() => handleOrgTypeSelection("Agency")}
                selected={preferredOrgType === "Agency"}
              />
            </div>
          )}

          <div className="mt-nf-ds-10 rounded-nf-ds-lg bg-card border border-border p-nf-ds-6 shadow-nf-ds-xs">
            <p className="font-nf-ds-sans text-[13px] text-nf-ds-gray-600">
              Need help? Email{" "}
              <a
                href="mailto:alec@newform.ai"
                className="text-nf-ds-purple-product hover:text-nf-ds-purple-700 underline underline-offset-2"
              >
                alec@newform.ai
              </a>
              .
            </p>
          </div>

          {discovered_organizations.length > 0 && (
            <button
              type="button"
              onClick={() => setShowCreateOptions(false)}
              className="self-start font-nf-ds-mono text-[10.5px] uppercase tracking-[0.12em] text-muted-foreground hover:text-nf-ds-text-strong mt-nf-ds-8 cursor-pointer"
            >
              ← Back to workspaces
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col grow w-full px-nf-ds-4 py-nf-ds-8 sm:px-nf-ds-12 sm:py-nf-ds-12">
      <div className="flex flex-col grow max-w-[600px] w-full mx-auto">
        <header className="flex flex-col gap-nf-ds-4 mb-nf-ds-10 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-nf-ds-1">
            <h1 className="font-nf-ds-serif text-[40px] leading-[1.05] tracking-[-0.02em] text-nf-ds-black">
              Your workspaces
            </h1>
            <p className="font-nf-ds-sans text-[15px] text-nf-ds-gray-600">
              Choose the organization you'd like to access.
            </p>
          </div>
          <div className="relative shrink-0 sm:mt-nf-ds-2">
            <Search className="absolute left-nf-ds-3 top-1/2 -translate-y-1/2 h-4 w-4 text-nf-ds-gray-500 pointer-events-none" />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search workspaces"
              className="h-10 w-full sm:w-[220px] rounded-nf-ds-2xl bg-card pl-nf-ds-10 shadow-nf-ds-xs"
            />
          </div>
        </header>

        {pendingAccountLink && (
          <LinkOldAccountCard
            targetEmail={pendingAccountLink.targetEmail}
            organizationCount={pendingAccountLink.organizationCount}
          />
        )}

        <div className="flex flex-col gap-nf-ds-8 grow">
          {agencyMemberships.length > 0 && (
            <section className="flex flex-col gap-nf-ds-4">
              <SectionDivider label="Agency" />
              <div className="flex flex-col gap-nf-ds-1">
                <h2 className="font-nf-ds-serif text-[22px] leading-[1.2] tracking-[-0.01em] text-nf-ds-black">
                  Manage your clients
                </h2>
                <p className="font-nf-ds-sans text-[13px] text-nf-ds-gray-600">
                  Start here if you operate clients through an agency account.
                </p>
              </div>
              <ul className="flex flex-col gap-nf-ds-3">
                {agencyMemberships.map((entry) => (
                  <OrgRowItem
                    key={entry.organization?.organization_id}
                    entry={entry}
                    isLoading={
                      navigatingToOrgId === entry.organization?.organization_id
                    }
                    disabled={navigatingToOrgId !== null}
                    onClick={() => handleOrgRowClick(entry)}
                  />
                ))}
              </ul>
            </section>
          )}

          {otherMemberships.length > 0 && (
            <section className="flex flex-col gap-nf-ds-4">
              <SectionDivider label="Other memberships" />
              <ul className="flex flex-col gap-nf-ds-3">
                {otherMemberships.map((entry) => (
                  <OrgRowItem
                    key={entry.organization?.organization_id}
                    entry={entry}
                    isLoading={
                      navigatingToOrgId === entry.organization?.organization_id
                    }
                    disabled={navigatingToOrgId !== null}
                    onClick={() => handleOrgRowClick(entry)}
                  />
                ))}
              </ul>
            </section>
          )}

          {flatRowsLength === 0 && (
            <div className="flex flex-col items-center gap-nf-ds-2 py-nf-ds-12 text-center">
              <p className="font-nf-ds-sans text-[14px] text-nf-ds-gray-600">
                No workspaces match "{search}".
              </p>
              <button
                type="button"
                onClick={() => setSearch("")}
                className="font-nf-ds-mono text-[10.5px] uppercase tracking-[0.12em] text-nf-ds-purple-product hover:text-nf-ds-purple-700"
              >
                Clear search
              </button>
            </div>
          )}
        </div>

        <footer className="mt-nf-ds-10 pt-nf-ds-6 border-t border-nf-ds-gray-200">
          <p className="font-nf-ds-sans text-[13px] text-nf-ds-gray-600">
            Don't see your org?{" "}
            <button
              type="button"
              onClick={handleCreateNewOrg}
              className="text-nf-ds-text-strong underline underline-offset-2 hover:text-foreground cursor-pointer"
            >
              Request access or create an organization
            </button>
          </p>
        </footer>
      </div>
    </div>
  );
};

function SectionDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-nf-ds-4">
      <span className="font-nf-ds-mono text-[10.5px] uppercase tracking-[0.12em] text-nf-ds-gray-600 leading-none">
        {label}
      </span>
      <span className="flex-1 h-px bg-nf-ds-gray-200" />
    </div>
  );
}

type OrgRowItemProps = {
  entry: OrgRow;
  isLoading: boolean;
  disabled: boolean;
  onClick: () => void;
};

function OrgRowItem({ entry, isLoading, disabled, onClick }: OrgRowItemProps) {
  const { organization, membership, isAgency, logoUrl } = entry;
  const name = organization?.organization_name ?? "Unknown";
  const initial = name[0]?.toUpperCase() ?? "?";
  const isPending =
    membership?.type === "pending_member" ||
    membership?.type === "invited_member" ||
    membership?.type === "eligible_to_join_by_email_domain";

  return (
    <li>
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className="group w-full flex items-center justify-between gap-nf-ds-4 bg-card rounded-nf-ds-2xl border border-border hover:border-nf-ds-purple-product/50 focus-visible:border-nf-ds-purple-product focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-nf-ds-purple-product/30 px-nf-ds-4 py-nf-ds-3 shadow-nf-ds-xs transition-colors disabled:opacity-60 disabled:cursor-not-allowed disabled:pointer-events-none cursor-pointer"
      >
        <div className="flex items-center gap-nf-ds-4 min-w-0">
          {logoUrl ? (
            <div className="flex h-10 w-10 items-center justify-center rounded-full overflow-hidden bg-card border border-border shrink-0">
              {/* biome-ignore lint/performance/noImgElement: signed URLs rotate each load; Next/Image's optimizer adds no value here */}
              <img
                src={logoUrl}
                alt={`${name} logo`}
                className="size-full object-contain"
              />
            </div>
          ) : (
            <div
              className="flex h-10 w-10 items-center justify-center rounded-full text-nf-ds-white font-nf-ds-sans text-[15px] font-[600] shrink-0"
              style={{ background: WORKSPACE_AVATAR_GRADIENT }}
            >
              {initial}
            </div>
          )}
          <div className="flex flex-col items-start min-w-0">
            <div className="flex items-center gap-nf-ds-2">
              <span className="font-nf-ds-sans text-[15px] font-[600] text-foreground truncate">
                {name}
              </span>
              {isAgency && (
                <span className="font-nf-ds-mono text-[9px] uppercase tracking-[0.12em] text-nf-ds-purple-product bg-nf-ds-purple-product/[0.08] rounded-nf-ds-sm px-nf-ds-2 py-nf-ds-1 leading-none">
                  Agency
                </span>
              )}
            </div>
            <span className="flex items-center gap-nf-ds-2 font-nf-ds-sans text-[12px] text-nf-ds-gray-600 mt-nf-ds-1">
              {isPending && (
                <span className="w-1.5 h-1.5 rounded-full bg-nf-ds-purple-product" />
              )}
              {isPending ? "Pending invite" : "Existing member"}
            </span>
          </div>
        </div>
        {isLoading ? (
          <Loader2 className="h-5 w-5 text-nf-ds-purple-product animate-spin shrink-0" />
        ) : (
          <ArrowRight className="h-5 w-5 text-nf-ds-gray-400 group-hover:text-nf-ds-text-body group-hover:translate-x-0.5 transition-all shrink-0" />
        )}
      </button>
    </li>
  );
}

type CreateOrgCardProps = {
  title: string;
  description: string;
  href: string;
  selected: boolean;
  onClick: () => void;
};

function CreateOrgCard({
  title,
  description,
  href,
  selected,
  onClick,
}: CreateOrgCardProps) {
  return (
    <Link
      href={href}
      onClick={onClick}
      className={`group flex flex-col gap-nf-ds-2 bg-card rounded-nf-ds-2xl border p-nf-ds-6 shadow-nf-ds-xs transition-colors ${
        selected
          ? "border-nf-ds-purple-product ring-2 ring-nf-ds-purple-product/30"
          : "border-nf-ds-gray-200 hover:border-nf-ds-purple-product/50"
      }`}
    >
      <h3 className="font-nf-ds-serif text-[22px] leading-[1.2] tracking-[-0.01em] text-foreground">
        {title}
      </h3>
      <p className="font-nf-ds-sans text-[13px] text-nf-ds-gray-600">
        {description}
      </p>
      <span className="inline-flex items-center gap-nf-ds-1 font-nf-ds-mono text-[10.5px] uppercase tracking-[0.12em] text-nf-ds-purple-product mt-nf-ds-3">
        Continue
        <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
      </span>
    </Link>
  );
}
